import { configurationContainer } from "@/configurationContainer";
import { createCoreUILinks, createProdaLinks } from "@/utils/createProdaLink";
import { areThirdPartyToolsEnabled } from "@/utils/getEnvironment";
import {
    Icon,
    IconBox,
    NavigationSidebar,
    SidebarItem,
    SidebarSection,
    Text,
    Tooltip,
} from "@proda-ai/ui-kit";
import React from "react";
import Zendesk from "react-zendesk";
import { Userpilot } from "userpilot";

const PATHS = {
    MANAGE: {
        FILES: "/rr/database/list/files",
        PROPERTIES: "/rr/database/list/properties",
        PROJECTS: "/projects",
    },
    ANALYSE: {
        RENT_ROLLS: "/rr/reports/database/rentRoll",
        EXPLORER: "/rr/database/explorer",
    },
    DATA_GOVERNANCE: {
        BASE: "/rr/data-governance/",
        APPROVALS: "/rr/data-governance/#/approvals",
        TRACKER: "/rr/data-governance/#/tracker",
    },
    TENANT_MANAGEMENT: "/rr/reports/database/tenantmanagement",
    UNIT_MANAGEMENT: "/rr/reports/database/unitmanagement",
};

export type ViewNavigationSidebarParams = {
    ViewSettingsMenu: React.FC;
    useState: () => {
        settingsOpen: boolean;
        isProdaUser: boolean;
        isAdmin: boolean;
        userSelectedDatabases: number[];
        hadDataGovernanceAccess: boolean;
        dataGovernanceEnabledDbIds: number[];
        newUiEnabled: boolean;
    };
};
export type ViewNavigationSidebarProps = {
    fileUploadModalOpen?: boolean;
    enableUploadButton?: boolean;
    handleUploadFileButtonClicked?: () => void;
};

export const testId = {
    UploadButtonTooltip: "upload-button-tooltip",
    UploadButton: "upload-button",
    GetHelp: "get-help",
};

export const createViewNavigationSidebar = ({
    ViewSettingsMenu,
    useState,
}: ViewNavigationSidebarParams) => {
    const ViewNavigationSidebarContent = (
        props: ViewNavigationSidebarProps,
    ) => {
        const {
            settingsOpen,
            userSelectedDatabases,
            hadDataGovernanceAccess,
            dataGovernanceEnabledDbIds,
            newUiEnabled,
        } = useState();
        const {
            handleUploadFileButtonClicked,
            fileUploadModalOpen = false,
            enableUploadButton = false,
        } = props;

        const uploadFileClicked = () => {
            if (!handleUploadFileButtonClicked || !enableUploadButton) return;
            handleUploadFileButtonClicked();
        };

        const getHelpAvailable = areThirdPartyToolsEnabled();

        const showProjects = newUiEnabled;
        const manageSection = () => {
            return (
                <>
                    {showProjects && (
                        <SidebarItem
                            id="projects"
                            type="item"
                            label="Projects"
                            icon="folder"
                            href={createCoreUILinks(PATHS.MANAGE.PROJECTS)}
                        />
                    )}
                    {newUiEnabled ? null : (
                        <>
                            <SidebarItem
                                id="properties"
                                type="item"
                                label="Properties"
                                icon="buildings"
                                isDisabled
                            />
                            <SidebarItem
                                id="tenants"
                                type="item"
                                label="Tenants"
                                icon="people-group"
                                href={createProdaLinks(
                                    PATHS.TENANT_MANAGEMENT,
                                    userSelectedDatabases,
                                )}
                            />
                            <SidebarItem
                                id="units"
                                type="item"
                                label="Units"
                                icon="door-closed"
                                href={createProdaLinks(
                                    PATHS.UNIT_MANAGEMENT,
                                    userSelectedDatabases,
                                )}
                            />
                            <SidebarItem
                                id="files"
                                type="item"
                                label="Files"
                                icon="rectangle-history"
                                isDisabled
                            />
                        </>
                    )}
                </>
            );
        };

        return (
            <NavigationSidebar
                bottomChildren={
                    <>
                        <SidebarSection
                            withDivider={!newUiEnabled}
                            id="settings"
                            heading={{ type: "none", title: "" }}
                        >
                            <SidebarItem
                                id="get-help"
                                data-testid={testId.GetHelp}
                                icon="question-circle"
                                label="Get Help"
                                isDisabled={!getHelpAvailable}
                                onClick={() => {
                                    Userpilot.trigger(
                                        "resource_center:ChrlsPeRES",
                                    );
                                }}
                            />
                            <ViewSettingsMenu />
                        </SidebarSection>
                    </>
                }
            >
                {newUiEnabled ? null : (
                    <Tooltip
                        content={
                            !enableUploadButton
                                ? "Upload disabled on this page"
                                : "Upload"
                        }
                        placement="right"
                        style={{
                            width: "100%",
                        }}
                        data-testid={testId.UploadButtonTooltip}
                    >
                        <SidebarItem
                            icon="upload"
                            label="Upload"
                            id="upload-button"
                            data-testid={testId.UploadButton}
                            onClick={uploadFileClicked}
                            current={fileUploadModalOpen}
                            isDisabled={!enableUploadButton}
                        />
                    </Tooltip>
                )}
                {newUiEnabled ? (
                    manageSection()
                ) : (
                    <SidebarSection
                        withDivider
                        id="manage"
                        heading={{ type: "label", title: "Manage" }}
                    >
                        {manageSection()}
                    </SidebarSection>
                )}
                {newUiEnabled ? null : (
                    <SidebarSection
                        withDivider
                        id="analyse"
                        heading={{ type: "label", title: "Analyse" }}
                    >
                        <SidebarItem
                            id="rentroll-list"
                            type="item"
                            label="Rent Rolls"
                            icon="scroll"
                            href={createProdaLinks(
                                PATHS.ANALYSE.RENT_ROLLS,
                                userSelectedDatabases,
                            )}
                        />
                        <SidebarItem
                            id="odin-link"
                            type="item"
                            label="Operating Statements"
                            icon="file-spreadsheet"
                            href="/"
                            current={!fileUploadModalOpen && !settingsOpen}
                        />
                        <SidebarItem
                            id="explorer-page"
                            type="item"
                            label="Explorer"
                            icon="chart-pie"
                            href={createProdaLinks(
                                PATHS.ANALYSE.EXPLORER,
                                userSelectedDatabases,
                            )}
                        />
                    </SidebarSection>
                )}

                {newUiEnabled ? null : (
                    <SidebarSection
                        withDivider
                        id="data-governance"
                        heading={{
                            type: "label",
                            title: "Audit",
                        }}
                    >
                        {!hadDataGovernanceAccess ? (
                            <SidebarItem
                                id="data-governance-overview"
                                type="item"
                                label="Overview"
                                icon="shield-halved"
                                href={createProdaLinks(
                                    PATHS.DATA_GOVERNANCE.BASE,
                                    userSelectedDatabases,
                                )}
                                trailing={
                                    <Text
                                        pattern="b4"
                                        color="proda-orange"
                                        weight="medium"
                                        wrap="nowrap"
                                    >
                                        <IconBox mr={0.5}>
                                            <Icon
                                                icon="sparkles"
                                                color="proda-orange"
                                            />
                                        </IconBox>
                                        Upgrade
                                    </Text>
                                }
                            />
                        ) : (
                            <>
                                <SidebarItem
                                    id="data-governance-approve"
                                    type="item"
                                    label="Approvals"
                                    icon="shield-halved"
                                    href={createProdaLinks(
                                        PATHS.DATA_GOVERNANCE.APPROVALS,
                                        dataGovernanceEnabledDbIds,
                                    )}
                                />
                                <SidebarItem
                                    id="data-governance-tracker"
                                    type="item"
                                    label="Tracker"
                                    icon="clipboard-list-check"
                                    href={createProdaLinks(
                                        PATHS.DATA_GOVERNANCE.TRACKER,
                                        dataGovernanceEnabledDbIds,
                                    )}
                                />
                            </>
                        )}
                    </SidebarSection>
                )}
                {getHelpAvailable && (
                    <Zendesk
                        zendeskKey={configurationContainer.PUBLIC_ZENDESK_KEY}
                        webWidget={true}
                    />
                )}
            </NavigationSidebar>
        );
    };
    return ViewNavigationSidebarContent;
};
